<template>
  <button
    v-if="!props.href"
    :class="[
      `${$style[variant]} ${$style[size]} ${$style.buttonStyle} ${iconSize && $style[iconSize]}`,
      'type-label-large-emphasized',
    ]"
    :disabled="disabled"
    :type="type"
    :aria-label="ariaLabel"
    :aria-expanded="ariaExpanded"
  >
    <Transition
      name="icon-rotate"
      mode="out-in"
    >
      <component
        :is="leftIconComponent"
        v-if="leftIconComponent"
        :key="props.leftIconName"
        :class="[$style.buttonIcon, $style.buttonIconLeft]"
      />
    </Transition>
    {{ text }}
    <slot />
    <component
      :is="rightIconComponent"
      v-if="rightIconComponent"
      :class="[$style.buttonIcon, $style.buttonIconRight]"
    />
  </button>
  <a
    v-else
    :class="[
      `${$style[variant]} ${$style[size]} ${$style.buttonStyle}`,
      'type-label-large-emphasized',
    ]"
    :aria-disabled="disabled ? 'true' : undefined"
    :tabindex="disabled ? -1 : undefined"
    :aria-label="ariaLabel"
    :aria-expanded="ariaExpanded"
    :href="props.href"
    :target="props.target"
    :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
    @click="
      (e: MouseEvent) => {
        if (disabled) e.preventDefault()
      }
    "
  >
    <component
      :is="leftIconComponent"
      v-if="leftIconComponent"
      :class="[$style.buttonIcon, $style.buttonIconLeft]"
    />
    {{ text }}
    <slot />
    <component
      :is="rightIconComponent"
      v-if="rightIconComponent"
      :class="[$style.buttonIcon, $style.buttonIconRight]"
    />
  </a>
</template>
<script setup lang="ts">
  import type { ButtonIconSize } from '~/constants/buttonTypes'
  import { ButtonSize, ButtonVariant } from '~/constants/buttonTypes'
  import { iconMap, type Icon, type IconName } from '~/constants/iconTypes'
  interface ButtonProps {
    size?: ButtonSize
    iconSize?: ButtonIconSize
    text?: string
    variant?: ButtonVariant
    disabled?: boolean
    leftIconName?: IconName
    rightIconName?: IconName
    href?: string
    target?: string
    type?: 'button' | 'submit' | 'reset'
    ariaLabel?: string
    ariaExpanded?: boolean
  }
  const props = withDefaults(defineProps<ButtonProps>(), {
    variant: ButtonVariant.Primary,
    size: ButtonSize.FullWidth,
    disabled: false,
    target: '_blank',
    type: 'button',
  })

  const leftIconComponent: ComputedRef<Icon | undefined> = computed(
    () => props.leftIconName && iconMap[props.leftIconName],
  )

  const rightIconComponent: ComputedRef<Icon | undefined> = computed(
    () => props.rightIconName && iconMap[props.rightIconName],
  )
</script>
<style module>
  .buttonStyle {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
    max-height: 100%;
    color: var(--color-text-on-brand);
    background-color: var(--color-interactive-primary);
    border: none;
    border-radius: var(--radius-sm);
    white-space: nowrap;
    text-align: center;
    padding: 0 var(--space-16);
    flex-shrink: 0;

    svg {
      height: 1.3rem;
      width: 1.3rem;
      fill: currentcolor;
      flex-shrink: 0;
    }
  }

  .buttonStyle:hover {
    cursor: pointer;
    background-color: var(--color-interactive-primary-hover);
  }

  .buttonStyle:active {
    background-color: var(--color-interactive-primary-selected);
  }

  .buttonStyle:focus-visible {
    outline: 2px solid var(--color-interactive-primary);
    outline-offset: 2px;
  }

  .buttonStyle:disabled,
  .buttonStyle[aria-disabled='true'] {
    background: var(--color-interactive-primary-disabled);
    color: var(--color-text-on-brand);
    cursor: not-allowed;

    svg {
      fill: var(--color-icon-disabled);
    }
  }

  .content {
    height: fit-content;
    width: fit-content;
  }

  .compact {
    height: var(--space-40);
    width: auto;
    min-width: calc(var(--space-48) * 2);
    padding: 0 var(--space-16);
  }

  .extraSmall {
    width: 50px;
  }

  .small {
    width: 100px;
  }

  .medium {
    width: 150px;
  }

  .large {
    width: 250px;
  }

  .extraLarge {
    width: 400px;
  }

  .full {
    width: 100%;
  }

  .danger {
    background-color: var(--color-status-error);
    color: var(--color-text-on-brand);
    border: none;

    svg {
      fill: var(--color-icon-on-brand);
    }
  }

  .danger:hover,
  .danger:active {
    background-color: var(--color-status-error);
  }

  .danger:disabled,
  .danger[aria-disabled='true'] {
    background-color: var(--color-status-error-subtle);
    color: var(--color-text-disabled);

    svg {
      fill: var(--color-icon-disabled);
    }
  }

  .secondary,
  .grey {
    background-color: var(--color-interactive-secondary);
    border: none;
    color: var(--color-text-primary);

    svg {
      fill: var(--color-icon-primary);
    }
  }

  .secondary:hover,
  .grey:hover {
    background-color: var(--color-interactive-secondary-hover);
  }

  .secondary:active,
  .grey:active {
    background-color: var(--color-interactive-secondary-selected);
  }

  .secondary:disabled,
  .secondary[aria-disabled='true'],
  .grey:disabled,
  .grey[aria-disabled='true'] {
    background-color: var(--color-interactive-secondary-disabled);
    color: var(--color-text-disabled);

    svg {
      fill: var(--color-icon-disabled);
    }
  }

  .gradient {
    background: var(--gradient-brand-bold);
  }

  .gradient:hover {
    background: var(--gradient-brand-medium);
  }

  .gradient:active {
    background: var(--gradient-brand-bold);
  }

  .gradient:disabled,
  .gradient[aria-disabled='true'] {
    background: var(--gradient-brand-subtle);
  }

  .outlineDanger,
  .outlineSecondary,
  .outline {
    background-color: var(--color-surface-default);
  }

  .outline,
  .outlineSecondary {
    background-color: var(--color-interactive-tertiary);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-primary);

    svg {
      fill: var(--color-icon-primary);
    }
  }

  .outlineSecondary:hover,
  .outline:hover {
    background-color: var(--color-interactive-tertiary-hover);
    border-color: var(--color-interactive-tertiary-hover-border);
  }

  .outlineSecondary:active,
  .outline:active {
    background-color: var(--color-interactive-tertiary-selected);
    border-color: var(--color-interactive-tertiary-selected-border);
  }

  .outlineSecondary:disabled,
  .outlineSecondary[aria-disabled='true'],
  .outline:disabled,
  .outline[aria-disabled='true'] {
    background-color: var(--color-interactive-tertiary-disabled);
    border-color: var(--color-border-subtle);
    color: var(--color-text-disabled);

    svg {
      fill: var(--color-icon-disabled);
    }
  }

  .outlineDanger {
    border: 1px solid var(--color-status-error);
    color: var(--color-status-error);

    svg {
      fill: var(--color-status-error);
    }
  }

  .outlineDanger:hover,
  .outlineDanger:active {
    background-color: var(--color-status-error-subtle);
  }

  .outlineDanger:disabled,
  .outlineDanger[aria-disabled='true'] {
    background-color: var(--color-status-error-subtle);
    border-color: var(--color-border-subtle);
    color: var(--color-text-disabled);

    svg {
      fill: var(--color-icon-disabled);
    }
  }

  .textSecondary,
  .text {
    background-color: transparent;
    border: none;
    box-shadow: var(--elevation-md);
    text-decoration: underline;
    text-decoration-color: transparent;
  }

  .text {
    composes: text-link from global;
    color: var(--color-interactive-link);
  }

  .textSecondary {
    composes: text-link from global;
    color: var(--color-interactive-primary);
  }

  .text:hover {
    background-color: transparent;
    color: var(--color-interactive-link-hover);
    text-decoration-color: var(--color-interactive-link-hover);
  }

  .textSecondary:hover {
    background-color: transparent;
    color: var(--color-interactive-primary-hover);
    text-decoration-color: var(--color-interactive-primary-hover);
  }

  .textSecondary:active {
    background-color: transparent;
    color: var(--color-interactive-primary-selected);
    text-decoration-color: var(--color-interactive-primary-selected);
  }

  .text:active {
    background-color: transparent;
    color: var(--color-interactive-link-hover);
    text-decoration-color: var(--color-interactive-link-hover);
  }

  .textSecondary:disabled,
  .textSecondary[aria-disabled='true'],
  .text:disabled,
  .text[aria-disabled='true'] {
    background-color: transparent;
    color: var(--color-interactive-link-disabled);
    text-decoration-color: transparent;
  }

  .buttonIcon {
    fill: currentcolor;
  }

  .buttonIconLeft {
    margin-right: var(--space-4);
  }

  .buttonIconRight {
    margin-left: var(--space-4);
  }

  .ghost {
    background-color: transparent;
    color: var(--color-text-primary);
    border: none;
    box-shadow: none;
    height: fit-content;

    svg {
      fill: var(--color-icon-primary);
    }
  }

  .ghost:hover {
    background-color: var(--color-interactive-tertiary-hover);
  }

  .ghost:active {
    background-color: var(--color-interactive-tertiary-selected);
  }

  .ghost:disabled,
  .ghost[aria-disabled='true'] {
    background-color: transparent;
    color: var(--color-text-disabled);

    svg {
      fill: var(--color-icon-disabled);
    }
  }

  .buttonStyle:disabled,
  .buttonStyle:active {
    box-shadow: var(--elevation-md);
    cursor: default;
  }

  .iconMedium {
    svg {
      height: 1.6rem;
      width: 1.6rem;
      flex-shrink: 0;
    }
  }

  .iconLarge {
    svg {
      height: 2rem;
      width: 2rem;
      flex-shrink: 0;
    }
  }
</style>
